import {
	API_ROOT,
	applyFieldChange,
	hasFieldIdentity,
	switchTableData,
} from "../../functions";
import React, { setGlobal, useEffect, useGlobal, useState } from "reactn";
import { Route, Switch, withRouter } from "react-router-dom";
import {
	faEdit,
	faFilePdf,
	faUserEdit,
} from "@fortawesome/free-solid-svg-icons";

import API from "../../api";
import Button from "../../components/Button";
import { useRef } from "react";
import Confirm from "../../components/Confirm";
import Container from "../../components/Container";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Footer from "../../components/Footer";
import Form from "../../components/Form";
import PrintRecord from "./PrintRecord";
// import { Prompt } from "react-router";
import Section from "../../components/Section";
import Status from "../../components/Status";
import Table from "../../components/Table";

const Main = ({
	match: {
		url,
		params: { id: record_id },
	},
	contract_id,
	history,
	canEdit,
	canArchive,
}) => {
	const [isDirty, setIsDirty] = useGlobal("isDirty");
	const [editMode, setEditMode] = useState(false);
	const [record, setRecord] = useState(null);
	const [formattedRecord, setFormattedRecord] = useState(null);
	const [completed, setCompleted] = useState();
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState(null);

	// The value of `completed` currently stored on the server
	const savedCompleted = useRef(undefined);

	useEffect(
		function () {
			(function () {
				if (record && record.title) setGlobal({ title: record.title });
			})();
		},
		[record]
	);

	const handleLeavePage = () => {
		return true;
	};

	useEffect(function () {
		(async function () {
			const { success, error, formattedRecord, record, isOwner } =
				await API.records.findOne(contract_id, record_id);

			if (success) {
				savedCompleted.current = record.completed;
				setCompleted(record.completed);
				setEditMode(isOwner);
				setRecord(record);
				setFormattedRecord(formattedRecord);
			} else setError(error);
		})();
	}, []);

	useEffect(
		function () {
			(async function () {
				// Skip if completed is undefined
				if (typeof completed === "undefined") return;

				// Only write when the checkbox actually changed. This endpoint
				// also recounts the contract's completed records, so firing it
				// on every page load is needless work for the server.
				if (completed === savedCompleted.current) return;

				const { success, error } = await API.records.updateOne(
					contract_id,
					record_id,
					{
						completed,
					}
				);

				if (success) savedCompleted.current = completed;
				else setError(error);
			})();
		},
		[completed, contract_id, record_id]
	);

	const handleFieldChange = (inputState) => {
		// Say so rather than writing the change somewhere arbitrary. Nothing in
		// a form legitimately reports a change without a `uid`.
		if (!hasFieldIdentity(inputState)) {
			console.error("Field change with no uid:", inputState);
			setError(
				"A field on this form could not be saved. Please tell an administrator which field you were editing."
			);

			return;
		}

		setRecord((previous) => ({
			...previous,
			fields: applyFieldChange(previous.fields, inputState),
		}));
		setIsDirty(true);

		if (!window.onbeforeunload) window.onbeforeunload = handleLeavePage;
	};

	const handleSave = async () => {
		// Don't send the 'completed' field since it's managed separately
		const { completed: _, ...recordWithoutCompleted } = record;

		setError(null);
		setIsSaving(true);

		try {
			const { success, error } = await API.records.updateOne(
				contract_id,
				record_id,
				recordWithoutCompleted
			);

			if (success) {
				setIsDirty(false);
				if (!window.onbeforeunload) window.onbeforeunload = null;

				history.push("/contracts");
			} else {
				// Stay on the page — leaving would throw away the user's work
				setError(error || "The record could not be saved.");
			}
		} catch (ex) {
			console.error(ex);
			setError(`The record could not be saved: ${ex}`);
		} finally {
			setIsSaving(false);
		}
	};

	const handleArchive = async () => {
		const { success, error } = await API.records.archiveOne(
			contract_id,
			record_id
		);

		if (success) history.push("/contracts");
		else setError(error);
	};

	const inline = {
		display: "inline-block",
		verticalAlign: "middle",
	};

	return (
		<React.Fragment>
			{/* <Prompt
				when={isDirty}
				message="You have unsaved changes, are you sure you want to leave?"
			/> */}
			<Container>
				<img
					style={{
						margin: "10px 0",
					}}
					width="125px"
					src="http://www.resinbonded.co.uk/wp-content/uploads/2017/09/logo.png"
				/>
				<Status error>{error}</Status>
				{formattedRecord ? (
					<>
						<Section style={{ clear: "both", overflow: "auto" }}>
							<Form.Group style={{ float: "left" }}>
								<Form.Checkbox
									style={inline}
									checked={completed}
									onChange={({ checked }) =>
										setCompleted(checked)
									}
								/>
								<Form.Label style={inline}>
									Mark as completed
								</Form.Label>
							</Form.Group>
							<Form.Group style={{ float: "right" }}>
								<h3 style={{ margin: "0" }}>
									{canEdit && !completed && (
										<a
											title={`Switch to ${
												editMode ? "read" : "edit"
											} mode`}
											onClick={() =>
												setEditMode(!editMode)
											}
											className="text-primary"
										>
											<FontAwesomeIcon
												icon={
													editMode
														? faUserEdit
														: faEdit
												}
											/>
										</a>
									)}

									<a
										style={{ marginLeft: "10px" }}
										title={`Download / Print record PDF`}
										href={
											API_ROOT +
											`/contract/${contract_id}/record/${record_id}/pdf`
										}
										target="_blank"
										className="text-primary"
									>
										<FontAwesomeIcon icon={faFilePdf} />
									</a>
									{/* )} */}
								</h3>
							</Form.Group>
						</Section>
						{editMode ? (
							<Form>
								{formattedRecord.map(({ title, fields }, i) => (
									<Section title={title} key={`section-${i}`}>
										{fields.map((formField, j) => {
											const recordField =
												record.fields.find(
													({ uid }) =>
														formField.uid === uid
												);

											let inherit =
												recordField || formField;

											return (
												<Form.Switch
													{...inherit}
													onChange={(state) =>
														handleFieldChange(state)
													}
													key={`field-${j}`}
												/>
											);
										})}
									</Section>
								))}
								<Button
									type="button"
									variant="primary"
									disabled={isSaving}
									onClick={() => handleSave()}
								>
									{isSaving ? "Saving..." : "Save"}
								</Button>
								<Status error>{error}</Status>
							</Form>
						) : (
							formattedRecord.map(({ title, fields }, i) => (
								<Section
									title={title}
									key={`record-table-${i}`}
								>
									<Table>
										<tbody>
											{fields.length ? (
												fields
													.sort(
														(a, b) => a.uid - b.uid
													)
													.map((field, index) => {
														return (
															<tr key={index}>
																<th
																	dangerouslySetInnerHTML={{
																		__html: field.label,
																	}}
																></th>
																<td>
																	{switchTableData(
																		field
																	)}
																</td>
															</tr>
														);
													})
											) : (
												<tr>
													<td>
														<Status empty />
													</td>
												</tr>
											)}
										</tbody>
									</Table>
								</Section>
							))
						)}
					</>
				) : (
					<Status.Switch data={record} />
				)}
				{canArchive && (
					<Confirm onConfirm={() => handleArchive()}>
						Archive record
					</Confirm>
				)}
			</Container>
			<Footer />
		</React.Fragment>
	);
};

const Record = (props) => (
	<Switch>
		<Route
			exact
			path={props.match.url}
			render={() => <Main {...props} />}
		/>
		<Route
			path={`${props.match.url}/print`}
			render={() => <PrintRecord {...props} />}
		/>
	</Switch>
);

export default withRouter(Record);
